import { FileText } from "@tamagui/lucide-icons";
import { type ReactNode, useContext } from "react";
import type { GestureResponderEvent } from "react-native";

import {
  useGetConversationCareplan,
  useGetStatusOfEncounterAssociatedWithCuraiVisit,
} from "@/api/generated";
import {
  type CuraiRedirectContent,
  EncounterStartType,
} from "@/api/generated/models";
import type { AssistantClientEvent } from "@/api/model/Conversation";
import { useIsChatMutationPending } from "@/api/mutations/conversation";
import {
  Button,
  ChatNote,
  LayoutContext,
  Link,
  Logo,
  Theme,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { medicalGroupName } from "@/config/brand";
import MessageSatisfaction from "@/conversation/MessageSatisfaction";
import CarePlanDisplay from "@/conversation/sidebar/CarePlan";
import CuraiConsentRedirect from "@/conversation/sidebar/CuraiConsentRedirect";
import WaitingRoom from "@/conversation/sidebar/WaitingRoom";
import VideoPreview from "@/conversation/video/VideoPreview";
import { useCurrentConversationId } from "@/hooks/conversation";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useVideoStore } from "@/store";

type VisitStatusCardProps = {
  content: CuraiRedirectContent;
  conversationId?: string;
  clinicRequestId?: string;
  clinicConversationId?: string | null;
  messageId: string;
  feedback: AssistantClientEvent["feedback"];
  feedbackComment?: string;
  maybeCopy: AssistantClientEvent["maybeCopy"];
  isLatest: boolean;
  fallback?: ReactNode;
};

export default function VisitStatusCard({
  content,
  conversationId: eventConversationId,
  clinicRequestId,
  clinicConversationId,
  messageId,
  feedback,
  feedbackComment,
  maybeCopy,
  isLatest,
  fallback,
}: VisitStatusCardProps) {
  const urlConversationId = useCurrentConversationId();
  const conversationId = eventConversationId ?? urlConversationId;
  const router = usePropagatedRouter();
  const { setModalContent } = useContext(LayoutContext);
  const { isVideoVisitStarted } = useVideoStore();
  const { value: keepConversationsOpen, loading: isKeepOpenLoading } =
    useFeatureFlag("keep-conversations-open", false);
  const { data: carePlanData, isLoading: carePlanLoading } =
    useGetConversationCareplan(
      conversationId as string,
      clinicRequestId ? { clinic_request_id: clinicRequestId } : undefined,
      { query: { enabled: Boolean(conversationId && clinicConversationId) } },
    );
  const { data: encounterStatusData, isLoading: encounterStatusLoading } =
    useGetStatusOfEncounterAssociatedWithCuraiVisit(
      clinicConversationId as string,
      clinicRequestId ? { clinic_request_id: clinicRequestId } : undefined,
      { query: { enabled: Boolean(clinicConversationId) } },
    );
  const isPending = useIsChatMutationPending();
  const showSatisfaction = (isLatest && !isPending) || !isLatest;

  if (isKeepOpenLoading || carePlanLoading || encounterStatusLoading) {
    return <ChatNote.Skeleton />;
  }
  const encounterStatus = encounterStatusData?.status;
  const encounterType =
    encounterStatusData?.type ?? EncounterStartType.TEXT_BASED;

  // if the clinical data has been deleted, show a message to the user
  if (content?.archived) {
    const copyableText =
      "You visited Curai Health.\n\nYou have elected to delete clinical data associated with your visit.";
    return (
      <>
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="You visited Curai Health."
            subtitle="You have elected to delete clinical data associated with your visit."
            button={
              <Button mode="primary" disabled={true}>
                Removed
              </Button>
            }
          />
        </Theme>
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(copyableText)}
          />
        )}
      </>
    );
  }

  // if visit is already updated, don't allow
  // any further interaction
  if (content.action_taken === "visit_updated") {
    const copyableText =
      "Your doctor has been updated\n\nWe've shared this update with your Curai Health clinician.";
    return (
      <>
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="Your doctor has been updated"
            subtitle="We've shared this update with your Curai Health clinician."
            button={
              <Button mode="primary" disabled={true}>
                Update complete
              </Button>
            }
          />
        </Theme>
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(copyableText)}
          />
        )}
      </>
    );
  }

  if (
    clinicConversationId &&
    (encounterStatus === "in-progress" || encounterStatus === "pending")
  ) {
    let onPress;
    if (encounterType === EncounterStartType.AUDIOVISUAL) {
      onPress = () => {
        if (encounterStatus === "in-progress") {
          router.navigate(`/conversation/${clinicConversationId}`);
        }
        if (!isVideoVisitStarted) {
          setModalContent(() => ({ onClose }: { onClose: () => void }) => (
            <VideoPreview
              onClose={onClose}
              curaiConversationId={clinicConversationId}
            />
          ));
        }
      };
    } else if (encounterStatus === "in-progress") {
      onPress = () => router.navigate(`/conversation/${clinicConversationId}`);
    } else {
      onPress = () =>
        setModalContent(() => ({ onClose }: { onClose: () => void }) => (
          <WaitingRoom
            onClose={onClose}
            encounterType={encounterType}
            curaiConversationId={clinicConversationId}
          />
        ));
    }
    return (
      <>
        <ChatNote
          Icon={Logo.Only}
          title="Resume clinic visit"
          onPress={onPress}
        />
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy("Resume clinic visit")}
          />
        )}
      </>
    );
  }

  if (
    encounterStatus === "incomplete" &&
    encounterStatusData?.can_be_resumed &&
    clinicConversationId
  ) {
    const title = `Your ${medicalGroupName} session is paused`;
    const subtitle =
      "Your clinician is waiting. Tap to resume your conversation.";
    return (
      <>
        <ChatNote
          Icon={Logo.Only}
          title={title}
          subtitle={subtitle}
          onPress={() =>
            setModalContent(() => () => (
              <CuraiConsentRedirect
                resume
                conversationId={clinicConversationId}
              />
            ))
          }
        />
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(`${title}\n\n${subtitle}`)}
          />
        )}
      </>
    );
  }

  if (encounterStatus === "incomplete") {
    const title = "You visited Curai Health.";
    const message = keepConversationsOpen
      ? "Your previous visit timed out. Tell me what you need and I can set up a new visit."
      : "Your previous visit timed out. To continue, you'll need to start a new chat.";
    const subtitle = keepConversationsOpen ? (
      message
    ) : (
      <>
        Your previous visit timed out. To continue, you'll need to{" "}
        <Link
          size="$button"
          href="/"
          onPress={(event: GestureResponderEvent) => event.stopPropagation()}
        >
          start a new chat
        </Link>
        .
      </>
    );
    return (
      <>
        <ChatNote.Active
          Icon={Logo.Only}
          title={title}
          subtitle={subtitle}
          onPress={() =>
            router.navigate(`/conversation/${clinicConversationId}`)
          }
        />
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(`${title}\n\n${message}`)}
          />
        )}
      </>
    );
  }

  if (encounterStatus === "completed") {
    const copyableText = `You visited Curai Health.${carePlanData?.diagnosis ? `\n\n${carePlanData.diagnosis}` : ""}`;
    return (
      <>
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="You visited Curai Health."
            subtitle={carePlanData?.diagnosis}
            onPress={() =>
              router.navigate(`/conversation/${clinicConversationId}`)
            }
            button={
              carePlanData && (
                <Button
                  mode="primary"
                  icon={FileText}
                  onPress={(event: GestureResponderEvent) => {
                    event.stopPropagation();
                    setModalContent(
                      () =>
                        ({ onClose }: { onClose: () => void }) => (
                          <CarePlanDisplay
                            onClose={onClose}
                            content={carePlanData}
                            senderFullNameWithTitle={
                              carePlanData.sender_full_name_with_title
                            }
                          />
                        ),
                      { dismissOnOverlayPress: true },
                    );
                  }}
                >
                  Care plan
                </Button>
              )
            }
          />
        </Theme>
        {showSatisfaction && (
          <MessageSatisfaction
            messageId={messageId}
            feedback={feedback}
            feedbackComment={feedbackComment}
            onCopy={() => maybeCopy(copyableText)}
          />
        )}
      </>
    );
  }

  return <>{fallback}</>;
}
